import { useSyncExternalStore } from 'react'

/**
 * Whether the browser believes it has a network connection.
 *
 * ── THE ASYMMETRY IS THE WHOLE POINT, AND IT DECIDES WHERE THIS IS USED ─────
 *
 * `navigator.onLine === false` is RELIABLE: the machine has no route, and
 * nothing the app sends can leave it. `navigator.onLine === true` is NOT — it
 * means an interface is up, which a captive portal, a dead gateway and a
 * firewalled network all satisfy.
 *
 * So this may be used to explain a failure that has already happened, and never
 * to predict one. `ProblemNotice` consults it only inside the branch that has
 * already established no response arrived, and the banner below only ever says
 * something while it is false.
 *
 * Getting that backwards is how `ProblemNotice` came to tell a user whose
 * connection was fine to check their connection, for a WAF 403 with an HTML
 * body (LOG.md, Stage 7b). This is the same mistake available from the other
 * direction.
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect`: the value is
 * external and can change between render and commit, which is exactly what this
 * hook exists to handle. `getServerSnapshot` returns true because a document
 * rendered somewhere with no `navigator` is not evidence of being offline.
 */
function subscribe(onChange: () => void): () => void {
  window.addEventListener('online', onChange)
  window.addEventListener('offline', onChange)
  return () => {
    window.removeEventListener('online', onChange)
    window.removeEventListener('offline', onChange)
  }
}

export function useOnline(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => navigator.onLine,
    () => true,
  )
}
